import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { MetricTitle } from '@components/overview-dashboard/styled';
import { HelpPopup, useHelpPopup } from '@components/overview-dashboard/common/help-popup';
import { fetchData } from '@store/overviewDashboard/audienceMetrics/actions';
import {
  audienceMetricDataSelector,
  loadingSelector,
  audienceMetricsSelector,
} from '@store/overviewDashboard/audienceMetrics/selectors';
import { Retry } from '@components/common';
import { StyledParaHeader, StyledPara, AudienceWidgetWrapper } from './styled';
import AudienceMetricCard from './AudienceMetricCard';
import { MetricContent, TitleContainer } from '../metrics-widget/styled';
import { getMetricMetaByKey, getMetricOvbDisplayName } from '@src/utils/metrics';
import useSetExport from '@src/components/export/useSetExport';

const AudienceMetricWidget = () => {
  const dispatch = useDispatch();
  const audienceMetricData: any = useSelector(audienceMetricDataSelector);
  const audienceMetrics: any = useSelector(audienceMetricsSelector);
  const { helpContext, HelpIcon } = useHelpPopup();

  useEffect(() => {
    dispatch(fetchData());
  }, []);

  const renderHelpText = () => {
    const data: any = [];
    audienceMetrics.map((metricKey) => {
      const metricInfo = getMetricMetaByKey({ metricKey });
      const ovbDisplayName = getMetricOvbDisplayName({ metricKey });
      const header = `${ovbDisplayName} :`;
      const help = metricInfo.helpText;
      data.push(
        <>
          <StyledParaHeader>{header}</StyledParaHeader>
          <StyledPara>{help}</StyledPara>
        </>,
      );
    });
    return data;
  };

  const renderWidgetTitle = () => (
    <TitleContainer style={{ margin: '0px 10px' }}>
      <MetricTitle>Audience</MetricTitle>
      <HelpIcon />
    </TitleContainer>
  );

  const { loading, showError } = useSelector(loadingSelector);

  // TODO: confirm. render available metrics?
  const isEmpty = useMemo(
    () =>
      audienceMetrics.every(
        (metric) => get(audienceMetricData, [metric, 'humanizedNumber'], null) === null,
      ),
    [audienceMetricData],
  );

  const fetchDataOnRetry = useCallback(() => {
    dispatch(fetchData());
  }, []);

  const renderAudienceMetrics = () =>
    Object.values(audienceMetricData).map((eachMetric, index) => (
      <AudienceMetricCard key={index} metricData={eachMetric} />
    ));

  // Export data
  const exportHeaders = useMemo(() => {
    return [
      { key: 'metric', title: 'Metric' },
      { key: 'value', title: 'Value' },
    ];
  }, []);

  const exportData = useMemo(() => {
    return audienceMetrics.map((metricKey) => {
      const metricData = audienceMetricData[metricKey] || {};
      const ovbDisplayName = getMetricOvbDisplayName({ metricKey });
      const rawValue = metricData.rawValue;
      const fullValue = rawValue !== undefined && rawValue !== null ? rawValue : 'N/A';
      return {
        metric: ovbDisplayName,
        value: fullValue,
      };
    });
  }, [audienceMetricData, audienceMetrics]);

  useSetExport({
    pageKey: 'overview',
    sectionKey: 'overview-audience-metrics',
    widgetKey: 'audience-metrics',
    widgetInfo: { title: 'Audience Metrics' },
    headers: exportHeaders,
    data: exportData,
  });

  return (
    <AudienceWidgetWrapper id="audience-metric-widget">
      {renderWidgetTitle()}
      <MetricContent style={{ justifyContent: 'space-evenly' }}>
        <HelpPopup title="Audience Metrics" helpText={renderHelpText()} helpContext={helpContext} />
        <Retry
          isLoading={loading}
          showError={showError}
          fetchData={fetchDataOnRetry}
          isEmpty={isEmpty}
        >
          <React.Fragment key="audience-charts">{renderAudienceMetrics()}</React.Fragment>
        </Retry>
      </MetricContent>
    </AudienceWidgetWrapper>
  );
};

export default AudienceMetricWidget;
